import { c as _c } from "react/compiler-runtime";
import { scaleLinear, ScaleLinear } from 'd3-scale';
import { Fmt } from '../Fmt';
import { match, P } from 'ts-pattern';
import { twMerge } from 'tailwind-merge';
import { isEmpty, reverse, sortBy } from 'remeda';
import { Markdown, StyleProps, Tip, useElements, useElementSize, WrapIf } from '../../ui';
import { FmtLabel } from '../FmtLabel';
import { CSSProperties, Fragment, ReactNode, useMemo } from 'react';
import { max } from '../../agg';
import { useMergedRef } from '@mantine/hooks';
import { QRComparison } from './questionResultsLayout';
import { isNullOrEmpty } from '../../collection';
import { uri } from '../../uri';
import { ComparisonType } from '../comparisonType';
import { HiOutlineExternalLink } from 'react-icons/hi';
type Rect = {
  x: number;
  y: number;
  w: number;
  h: number;
};
const rectOverlaps = (a: Rect, b: Rect) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
export type { QRComparison };
const useQRComparisons = props => {
  const $ = _c(19);
  let t0;
  if ($[0] !== props.comparisons) {
    t0 = props.comparisons?.map(_temp) ?? [];
    $[0] = props.comparisons;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    elById,
    ref: elsRef
  } = useElements(t0);
  const {
    ref: sizeRef,
    width
  } = useElementSize();
  const rootRef = useMergedRef(elsRef, sizeRef);
  let t1;
  if ($[2] !== props.scale || $[3] !== width) {
    t1 = props.scale == "auto-likelihood" ? width > 0 ? scaleLinear().domain([0, 100]).range([0, width]) : undefined : props.scale;
    $[2] = props.scale;
    $[3] = width;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const scale = t1;
  let t2;
  let t3;
  if ($[5] !== elById || $[6] !== props.comparisons || $[7] !== scale || $[8] !== width) {
    const layoutCurrent = sortBy(props.comparisons, [_temp2, "asc"]).map((c_0, i) => {
      const el = elById?.[c_0.id];
      return match({
        el,
        scale
      }).with({
        el: P.nonNullable,
        scale: P.nonNullable
      }, t4 => {
        const {
          el: el_0,
          scale: scale_0
        } = t4;
        const x = scale_0(c_0.value);
        const w = el_0.clientWidth;
        const h = el_0.clientHeight;
        const align = x > width * 0.8 ? "right" : i == 0 && props.comparisons.length > 1 && x - w > 0 ? "right" : "left";
        const rect = {
          x: align == "right" ? x - w + 3 : x - 3,
          y: 3,
          w,
          h
        };
        return {
          ready: true,
          ...c_0,
          rect,
          align,
          x
        } as const;
      }).otherwise(() => ({
        ...c_0,
        ready: false
      }) as const);
    });
    const itemsReady = reverse(layoutCurrent.filter(_temp3));
    itemsReady.forEach((c_2, i_0) => {
      let attempts = 0;
      while (true) {
        const inConflict = itemsReady.filter(b => b != c_2).find(b_0 => rectOverlaps(b_0.rect, c_2.rect));
        if (!inConflict || attempts++ > 6) {
          break;
        }
        if (inConflict.rect.x < c_2.rect.x) {
          inConflict.rect.y = inConflict.rect.y + (c_2.rect.h + 2);
        } else {
          c_2.rect.y = c_2.rect.y + (inConflict.rect.h + 2);
        }
      }
    });
    t3 = layoutCurrent;
    t2 = max(layoutCurrent.filter(_temp4).map(_temp5));
    $[5] = elById;
    $[6] = props.comparisons;
    $[7] = scale;
    $[8] = width;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  const t4 = t2 + 4;
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = {
      items: t3,
      height: t4
    };
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const layout = t5;
  let t6;
  if ($[14] !== elById || $[15] !== layout || $[16] !== rootRef || $[17] !== scale) {
    t6 = {
      layout,
      rootRef,
      elById,
      scale
    };
    $[14] = elById;
    $[15] = layout;
    $[16] = rootRef;
    $[17] = scale;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
type QRComparisonsProps = {
  comparisons: QRComparison[];
  className?: string;
  scale: ScaleLinear<number, number, never> | 'auto-likelihood';
  fmt: Fmt.Option;
};
export const QRComparisons = props => {
  const $ = _c(21);
  const {
    className,
    fmt
  } = props;
  const {
    rootRef,
    elById,
    layout: t0
  } = useQRComparisons(props);
  const {
    height,
    items
  } = t0;
  let t1;
  if ($[0] !== height) {
    t1 = {
      height
    };
    $[0] = height;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = elById ? "overflow-x-clip" : "invisible flex gap-2";
  let t3;
  if ($[2] !== className || $[3] !== t2) {
    t3 = twMerge("relative", t2, className);
    $[2] = className;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== fmt || $[6] !== items) {
    let t5;
    if ($[8] !== fmt) {
      t5 = c => {
        const {
          id,
          value,
          description,
          title,
          color: colorRaw,
          ready,
          url,
          type
        } = c;
        const color = colorRaw ?? "color-mix(in srgb, var(--mantine-color-text), transparent 30%)";
        const tipItem = {
          title,
          url,
          type,
          value,
          description,
          color
        };
        return <WrapIf key={id} if={!isNullOrEmpty(description)} wrap={children => <ComparisonTip item={tipItem} fmt={fmt}>{children}</ComparisonTip>}><div id={id} className={twMerge("max-w-[16em] min-w-[10em] rounded-md pb-1", ready && "absolute")} style={ready ? {
            left: c.rect.x,
            top: c.rect.y,
            backgroundColor: "color-mix(in srgb, var(--mantine-color-body), transparent 10%)",
            textAlign: c.align
          } : undefined}><div className="flex gap-1" style={ready ? match(c.align).with("left", _temp6).with("right", _temp7).exhaustive() : undefined}><ValueWithUnit {...{
                fmt,
                value,
                color
              }} /><div className="opacity-70 font-semibold text-sm leading-tight pt-[0px]" style={{
                color
              }}><WrapIf if={!isNullOrEmpty(url)} wrap={c_0 => url ? <a href={url} target="_blank">{title}</a> : <>{title}</>}>{title}</WrapIf></div></div></div></WrapIf>;
      };
      $[8] = fmt;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = items.map(t5);
    $[5] = fmt;
    $[6] = items;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[10] !== fmt || $[11] !== items) {
    let t6;
    if ($[13] !== fmt) {
      t6 = c_2 => {
        const backgroundColor = c_2.color ?? (c_2.type ? comparisonColor(c_2.type) : "var(--mantine-color-gray-3)");
        const tipItem_0 = {
          title: c_2.title,
          url: c_2.url,
          type: c_2.type,
          value: c_2.value,
          description: c_2.description,
          color: c_2.color ?? (c_2.type ? comparisonColor(c_2.type) : "var(--mantine-color-gray-3)")
        };
        return <Fragment key={c_2.id}><div className="absolute w-[3px] rounded-lg" style={{
            top: c_2.rect.y - 2,
            height: 6,
            left: c_2.x,
            transform: "translateX(-50%)",
            backgroundColor
          }} /><ComparisonTip item={tipItem_0} fmt={fmt}><div className="absolute size-[16px] rounded-full" style={{
              top: c_2.rect.y + 12,
              left: c_2.x,
              transform: "translate(-50%, -50%)",
              outline: `2px solid ${backgroundColor}`
            }}><DomainIcon className="size-[16px] rounded-full" url={c_2.url} /></div></ComparisonTip></Fragment>;
      };
      $[13] = fmt;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t5 = items.filter(_temp8).map(t6);
    $[10] = fmt;
    $[11] = items;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[15] !== rootRef || $[16] !== t1 || $[17] !== t3 || $[18] !== t4 || $[19] !== t5) {
    t6 = <div ref={rootRef} style={t1} className={t3}>{t4}{t5}</div>;
    $[15] = rootRef;
    $[16] = t1;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
};
export const DomainIcon = t0 => {
  const $ = _c(4);
  const {
    url,
    className,
    style
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== style || $[2] !== url) {
    t1 = url ? <img src={`https://www.google.com/s2/favicons?domain=${url ? uri(url).hostSansPort : ""}&sz=32`} className={twMerge("h-[1em]", className)} style={style} /> : <div className={twMerge("size-[1em] bg-gray-50", className)} style={style} />;
    $[0] = className;
    $[1] = style;
    $[2] = url;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export const ComparisonLink = t0 => {
  const $ = _c(8);
  const {
    url,
    children
  } = t0;
  if (!url) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== url) {
    t1 = <DomainIcon url={url} className="size-5" />;
    $[2] = url;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== children || $[5] !== t1 || $[6] !== url) {
    t2 = <a target="_blank" href={url} className="font-semibold flex gap-2 items-center text-sm">{t1}{children}</a>;
    $[4] = children;
    $[5] = t1;
    $[6] = url;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export const comparisonColor = (type: ComparisonType) => match(type).with('base-rate', () => 'var(--mantine-color-blue-6)').with('forecast', () => 'var(--mantine-color-orange-6)').exhaustive();
export const ComparisonTypeBadge = t0 => {
  const $ = _c(25);
  const {
    type,
    longLabel,
    className,
    variant
  } = t0;
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== className || $[1] !== longLabel || $[2] !== type || $[3] !== variant) {
    const color = comparisonColor(type);
    T0 = WrapIf;
    t3 = !longLabel;
    if ($[9] !== type) {
      t4 = c => <Tip label={type}>{c}</Tip>;
      $[9] = type;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    if ($[11] !== className) {
      t1 = twMerge("inline-flex items-center px-2 py-1 rounded-lg text-[--mantine-color-body] text-xs font-extrabold", className);
      $[11] = className;
      $[12] = t1;
    } else {
      t1 = $[12];
    }
    t2 = match(variant ?? "light").with("filled", () => ({
      color: "var(--mantine-color-text)",
      backgroundColor: color
    })).with("light", () => ({
      color,
      backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)`,
      border: `2px solid color-mix(in srgb, ${color} 40%, transparent)`
    })).exhaustive();
    $[0] = className;
    $[1] = longLabel;
    $[2] = type;
    $[3] = variant;
    $[4] = T0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    T0 = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[13] !== longLabel || $[14] !== type) {
    t5 = longLabel ? type : type[0]?.toUpperCase();
    $[13] = longLabel;
    $[14] = type;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t1 || $[17] !== t2 || $[18] !== t5) {
    t6 = <div className={t1} style={t2}>{t5}</div>;
    $[16] = t1;
    $[17] = t2;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== T0 || $[21] !== t3 || $[22] !== t4 || $[23] !== t6) {
    t7 = <T0 if={t3} wrap={t4}>{t6}</T0>;
    $[20] = T0;
    $[21] = t3;
    $[22] = t4;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
};

// New component for value/unit display
const ValueWithUnit = props => {
  const $ = _c(12);
  const t0 = props.fmt.unit && props.fmt.unit.length > 2 ? "flex flex-col leading-none" : "";
  let t1;
  if ($[0] !== t0) {
    t1 = twMerge("mt-[-1px] leading-tight", t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      value: "opacity-100"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== props.color) {
    t3 = {
      value: {
        color: props.color
      }
    };
    $[3] = props.color;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== props.fmt || $[6] !== props.value || $[7] !== t3) {
    t4 = <FmtLabel {...props.fmt} value={props.value} classNames={t2} styles={t3} />;
    $[5] = props.fmt;
    $[6] = props.value;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <div className={t1}>{t4}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};

// New component for the comparison tooltip
const ComparisonTip = t0 => {
  const $ = _c(31);
  const {
    item,
    fmt,
    children
  } = t0;
  const color = item.color ?? "color-mix(in srgb, var(--mantine-color-text), transparent 30%)";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      shift: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== item.url) {
    t2 = isNullOrEmpty(item.url);
    $[1] = item.url;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = !t2;
  let t4;
  if ($[3] !== item.url) {
    t4 = c => <a href={item.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 font-bold text-md leading-tight" style={{
      color: "var(--mantine-color-text)"
    }}>{c}<HiOutlineExternalLink size={14} className="inline-block" /></a>;
    $[3] = item.url;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== item.title) {
    t5 = <div className="font-semibold">{item.title}</div>;
    $[5] = item.title;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t4 || $[9] !== t5) {
    t6 = <WrapIf if={t3} wrap={t4}>{t5}</WrapIf>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== item.type) {
    t7 = item.type && <ComparisonTypeBadge type={item.type} longLabel={true} variant="filled" />;
    $[11] = item.type;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <div>{t7}</div>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== color || $[16] !== fmt || $[17] !== item.value) {
    t9 = <ValueWithUnit {...{
      fmt,
      value: item.value,
      color
    }} />;
    $[15] = color;
    $[16] = fmt;
    $[17] = item.value;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <div className="flex gap-2">{t8}{t9}</div>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== item.description) {
    t11 = <Markdown className="text-[--mantine-color-text]">{item.description}</Markdown>;
    $[22] = item.description;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t6) {
    t12 = <div className="w-[min(30em,100vw)] space-y-2">{t6}{t10}{t11}</div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t6;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== children || $[29] !== t12) {
    t13 = <Tip middlewares={t1} label={t12}>{children}</Tip>;
    $[28] = children;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
};
function _temp(t) {
  return t.id;
}
function _temp2(c) {
  return c.value;
}
function _temp3(c_1) {
  return c_1.ready;
}
function _temp4(c_3) {
  return c_3.ready;
}
function _temp5(c_4) {
  return c_4.rect.y + c_4.rect.h;
}
function _temp6() {
  return {
    flexDirection: "row",
    paddingLeft: "16px",
    justifyItems: "flex-start"
  } satisfies CSSProperties;
}
function _temp7() {
  return {
    flexDirection: "row-reverse",
    paddingRight: "16px"
  } satisfies CSSProperties;
}
function _temp8(c_1) {
  return c_1.ready;
}